import { get } from 'aws-amplify/api';
import schema from './openapi.json';
import type { BasisSummary, LotDetail, LotFilters, LotPage } from './types';

export class CostBasisError extends Error {
  constructor(
    public status: number,
    message = 'Unable to load cost basis',
  ) {
    super(message);
  }
}

type Schema = {
  $ref?: string;
  anyOf?: Schema[];
  enum?: unknown[];
  const?: unknown;
  type?: string;
  required?: string[];
  properties?: Record<string, Schema>;
  items?: Schema;
  pattern?: string;
};
const definitions = schema.components.schemas as Record<string, Schema>;

// Validate the wire contract before values can reach financial displays.
function matches(value: unknown, rule: Schema): boolean {
  if (rule.$ref) return matches(value, definitions[rule.$ref.split('/').pop()!] || {});
  if (rule.anyOf) return rule.anyOf.some((part) => matches(value, part));
  if (rule.enum && !rule.enum.includes(value)) return false;
  if ('const' in rule && rule.const !== value) return false;
  if (rule.type === 'null') return value === null;
  if (rule.type === 'string')
    return (
      typeof value === 'string' &&
      value.length <= 4096 &&
      (!rule.pattern || new RegExp(rule.pattern).test(value))
    );
  if (rule.type === 'boolean') return typeof value === 'boolean';
  if (rule.type === 'integer') return typeof value === 'number' && Number.isSafeInteger(value);
  if (rule.type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (rule.type === 'array')
    return Array.isArray(value) && value.every((item) => matches(item, rule.items || {}));
  if (rule.type === 'object') {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const object = value as Record<string, unknown>;
    return (
      (rule.required || []).every((key) => key in object) &&
      Object.entries(rule.properties || {}).every(
        ([key, part]) => !(key in object) || matches(object[key], part),
      )
    );
  }
  return true;
}

async function request(
  apiName: 'ApexAPI' | 'ClientPortalAPI',
  path: string,
  queryParams: Record<string, string> = {},
): Promise<unknown> {
  try {
    const response = await get({ apiName, path, options: { queryParams } }).response;
    if (response.statusCode >= 400) throw new CostBasisError(response.statusCode);
    return JSON.parse(await response.body.text());
  } catch (error) {
    if (error instanceof CostBasisError) throw error;
    const status = (error as { response?: { statusCode?: number; status?: number } })?.response;
    throw new CostBasisError(status?.statusCode || status?.status || 0);
  }
}

export async function resolveBasisAccount(
  accountId: string,
  canonical: boolean,
): Promise<string | null> {
  if (!canonical) return accountId;
  const raw = await request(
    'ClientPortalAPI',
    `/api/wealth/accounts/${encodeURIComponent(accountId)}/cost-basis-access`,
  );
  const envelope = raw as { status?: string; data?: unknown };
  const data = (envelope?.status === 'success' ? envelope.data : raw) as {
    account_id?: string;
    source?: string;
    apex_account_id?: string;
    tax_lots_supported?: boolean;
  };
  if (!data || data.account_id !== accountId || typeof data.tax_lots_supported !== 'boolean')
    throw new CostBasisError(502);
  if (!data.tax_lots_supported) return null;
  if (data.source !== 'APEX' || !data.apex_account_id) throw new CostBasisError(502);
  return data.apex_account_id;
}

async function read<T>(
  accountId: string,
  suffix: string,
  name: string,
  params: Record<string, string>,
): Promise<T> {
  const data = await request(
    'ApexAPI',
    `/cost-basis/accounts/${encodeURIComponent(accountId)}/${suffix}`,
    params,
  );
  if (!matches(data, definitions[name])) throw new CostBasisError(502);
  const account =
    name === 'LotDetail'
      ? (data as LotDetail).record.account_id
      : (data as BasisSummary).account_id;
  if (
    account !== accountId ||
    (name === 'LotPage' && (data as LotPage).records.some((lot) => lot.account_id !== accountId))
  )
    throw new CostBasisError(502);
  return data as T;
}
const params = (filters: LotFilters): Record<string, string> =>
  Object.fromEntries(
    Object.entries(filters).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
export const getBasisSummary = (account: string, filters: LotFilters = {}) =>
  read<BasisSummary>(account, 'summary', 'BasisSummary', params(filters));
export const getBasisLots = (account: string, filters: LotFilters, cursor?: string) =>
  read<LotPage>(account, 'lots', 'LotPage', {
    ...params(filters),
    limit: '64',
    ...(cursor ? { cursor } : {}),
  });
export const getBasisLot = async (account: string, lotId: string) => {
  const result = await read<LotDetail>(
    account,
    `lots/${encodeURIComponent(lotId)}`,
    'LotDetail',
    {},
  );
  if (result.record.lot_id !== lotId) throw new CostBasisError(502);
  return result;
};
